import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { assessmentService } from '@/lib/services/assessmentService';

interface Question {
  id: string;
  question: string;
  options: string[];
}

interface ForgivenessAssessmentProps {
  questions: Question[];
  currentQuestion: number;
  selectedAnswers: { [key: string]: string };
  onAnswerSelect: (questionId: string, answer: string) => void;
  onNext: () => void;
  onPrevious: () => void;
  onSubmit: () => void;
  showResults: boolean;
  assessmentScore: number;
}

const ForgivenessAssessment: React.FC<ForgivenessAssessmentProps> = ({
  questions,
  currentQuestion,
  selectedAnswers,
  onAnswerSelect,
  onNext,
  onPrevious,
  onSubmit,
  showResults,
  assessmentScore,
}) => {
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleSubmitWithSave = async () => {
    const level = assessmentScore >= 80 ? 'advanced_peace_builder' :
                  assessmentScore >= 60 ? 'growing_peacemaker' :
                  assessmentScore >= 40 ? 'beginning_journey' : 'starting_point';

    try {
      await assessmentService.submitResult({
        email: 'user@example.com',
        score: assessmentScore,
        level,
        answers: selectedAnswers,
      });
    } catch (error) {
      console.error('Failed to save assessment result:', error);
    }

    onSubmit();
  };

  if (showResults) {
    const getRecommendation = (score: number) => {
      if (score >= 80) {
        return {
          title: 'Advanced Peace Builder',
          description: 'You have a strong foundation in forgiveness and emotional healing. Consider becoming a community leader or mentor.',
          resources: [
            'Advanced Community Organizing Toolkit',
            'Leadership Development Program',
            'Mentor Training Materials',
          ],
          color: 'bg-success',
        };
      } else if (score >= 60) {
        return {
          title: 'Growing Peacemaker',
          description: 'You are on a positive journey toward forgiveness. Continue deepening your practice with guided resources.',
          resources: [
            'Intermediate Forgiveness Framework',
            '30-Day Transformation Guide',
            'Personal Reflection Journal',
          ],
          color: 'bg-peaceful-teal',
        };
      } else if (score >= 40) {
        return {
          title: 'Beginning Your Journey',
          description: 'You are taking important first steps. Focus on foundational forgiveness concepts and self-compassion.',
          resources: [
            'Introduction to Forgiveness',
            'Self-Compassion Workbook',
            'Guided Meditation Series',
          ],
          color: 'bg-transformation-gold',
        };
      } else {
        return {
          title: 'Starting Point',
          description: 'Every journey begins with a single step. We have resources specifically designed to support you at this stage.',
          resources: [
            'Healing from Hurt: A Beginner\'s Guide',
            'Understanding Forgiveness',
            'One-on-One Support Resources',
          ],
          color: 'bg-primary',
        };
      }
    };

    const recommendation = getRecommendation(assessmentScore);

    return (
      <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-24 h-24 ${recommendation.color} rounded-full mb-4`}>
            <span className="text-4xl font-headline font-bold text-white">{assessmentScore}</span>
          </div>
          <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
            {recommendation.title}
          </h3>
          <p className="text-base font-body text-muted-foreground max-w-2xl mx-auto">
            {recommendation.description}
          </p>
        </div>

        <div className="bg-muted rounded-lg p-6 mb-6">
          <h4 className="text-lg font-headline font-bold text-foreground mb-4 flex items-center">
            <Icon name="BookOpenIcon" size={24} className="text-primary mr-2" />
            Recommended Resources for You
          </h4>
          <div className="space-y-3">
            {recommendation.resources.map((resource, index) => (
              <div key={index} className="flex items-start space-x-3">
                <Icon name="CheckCircleIcon" size={20} className="text-success mt-0.5 flex-shrink-0" />
                <span className="text-sm font-body text-foreground">{resource}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => window.location.reload()}
            className="flex-1 py-3 bg-muted text-foreground rounded-md font-cta text-sm font-bold transition-all duration-300 hover:bg-muted/80"
          >
            Retake Assessment
          </button>
          <button className="flex-1 py-3 bg-primary text-primary-foreground rounded-md font-cta text-sm font-bold transition-all duration-300 hover:bg-secondary hover:scale-105">
            View Recommended Resources
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-body font-medium text-muted-foreground">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="text-sm font-body font-medium text-primary">{Math.round(progress)}%</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <h3 className="text-xl font-headline font-bold text-foreground mb-6">{question.question}</h3>

      <div className="space-y-3 mb-8">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => onAnswerSelect(question.id, option)}
            className={`w-full p-4 rounded-lg border-2 text-left font-body text-sm transition-all duration-200 ${
              selectedAnswers[question.id] === option
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border bg-card text-foreground hover:border-primary/50 hover:bg-muted'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  selectedAnswers[question.id] === option
                    ? 'border-primary bg-primary' :'border-muted-foreground'
                }`}
              >
                {selectedAnswers[question.id] === option && (
                  <div className="w-2.5 h-2.5 bg-white rounded-full" />
                )}
              </div>
              <span>{option}</span>
            </div>
          </button>
        ))}
      </div>

      <div className="flex justify-between">
        <button
          onClick={onPrevious}
          disabled={currentQuestion === 0}
          className="px-6 py-2.5 bg-muted text-foreground rounded-md font-cta text-sm font-bold transition-all duration-300 hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        {currentQuestion === questions.length - 1 ? (
          <button
            onClick={handleSubmitWithSave}
            disabled={!selectedAnswers[question.id]}
            className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Get Results
          </button>
        ) : (
          <button
            onClick={onNext}
            disabled={!selectedAnswers[question.id]}
            className="px-6 py-2.5 bg-primary text-primary-foreground rounded-md font-cta text-sm font-bold transition-all duration-300 hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default ForgivenessAssessment;